import React, { useState } from "react";
import { Link, Outlet, useNavigate } from "react-router-dom";
import { Menu, ShoppingCart, User } from "lucide-react";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "./ui/sheet";
import { CountdownBar } from "./Countdown";

const NAV = [
  { label: "Shop", to: "/shop" },
  { label: "Winter '26", to: "/shop?round=round-1", accent: "yellow" },
  { label: "Adult", to: "/shop?audience=Adult" },
  { label: "Women's", to: "/shop?audience=Women" },
  { label: "Youth", to: "/shop?audience=Youth" },
  { label: "Limited Edition", to: "/shop?collection=limited" },
  { label: "T-Shirts", to: "/shop?category=T-Shirts" },
  { label: "Hoodies", to: "/shop?category=Hoodies" },
  { label: "Rash Guards", to: "/shop?category=Rash%20Guards" },
  { label: "No-Gi", to: "/shop?category=No-Gi" },
  { label: "Kickboxing", to: "/shop?category=Kickboxing" },
  { label: "Gis", to: "/shop?category=Gis" },
  { label: "Headwear", to: "/shop?category=Headwear" },
  { label: "Accessories", to: "/shop?category=Accessories" },
  { label: "Academies", to: "/academies" },
];

function Logo() {
  return (
    <Link to="/" data-testid="nav-logo" className="flex items-center gap-2.5 shrink-0">
      <img src="/paragon-logo.jpeg" alt="Paragon BJJ" className="h-11 w-11 rounded-full" />
      <div className="leading-none hidden sm:block">
        <div className="font-heading font-black text-lg tracking-tight text-white uppercase">Paragon</div>
        <div className="font-mono text-[9px] tracking-[0.25em] text-yellow-400 uppercase">Team Store</div>
      </div>
    </Link>
  );
}

function AccountLink() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  if (!user)
    return (
      <Link to="/login" data-testid="nav-account" className="text-zinc-300 hover:text-yellow-400 transition-colors">
        <User className="h-5 w-5" />
      </Link>
    );
  const dest = user.role === "admin" ? "/admin" : user.role === "gym_owner" ? "/gym" : "/account";
  return (
    <div className="flex items-center gap-3">
      <Link to={dest} data-testid="nav-account" className="text-zinc-300 hover:text-yellow-400 transition-colors">
        <User className="h-5 w-5" />
      </Link>
      <button
        data-testid="nav-logout"
        onClick={async () => { await logout(); navigate("/"); }}
        className="hidden md:block text-[11px] font-mono uppercase tracking-wider text-zinc-500 hover:text-white"
      >
        Logout
      </button>
    </div>
  );
}

export function Layout() {
  const { count } = useCart();
  const [open, setOpen] = useState(false);

  return (
    <div className="min-h-screen flex flex-col bg-[#0A0A0C]">
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-black/85 border-b border-white/10">
        <CountdownBar />
        <div className="max-w-[1400px] mx-auto px-4 lg:px-8">
          <div className="flex items-center justify-between h-16 gap-4">
            <div className="flex items-center gap-3">
              <Sheet open={open} onOpenChange={setOpen}>
                <SheetTrigger asChild>
                  <button data-testid="mobile-menu-btn" className="lg:hidden text-white p-1">
                    <Menu className="h-6 w-6" />
                  </button>
                </SheetTrigger>
                <SheetContent side="left" className="bg-[#0A0A0C] border-white/10 w-[300px] p-0">
                  <SheetTitle className="sr-only">Paragon Menu</SheetTitle>
                  <div className="p-5 border-b border-white/10">
                    <Logo />
                  </div>
                  <nav className="flex flex-col p-3">
                    {NAV.map((n) => (
                      <Link
                        key={n.label}
                        to={n.to}
                        onClick={() => setOpen(false)}
                        data-testid={`mobile-nav-${n.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                        className={`px-3 py-3 font-heading font-bold uppercase tracking-wide border-b border-white/5 ${n.accent === "yellow" ? "text-yellow-400" : "text-zinc-200 hover:text-yellow-400"}`}
                      >
                        {n.label}
                      </Link>
                    ))}
                    <Link
                      to="/gym/login"
                      onClick={() => setOpen(false)}
                      className="mt-3 btn-green text-center px-4 py-3 rounded-sm text-sm"
                    >
                      Gym Owner Login
                    </Link>
                  </nav>
                </SheetContent>
              </Sheet>
              <Logo />
            </div>

            <div className="flex items-center gap-4">
              <Link
                to="/gym/login"
                data-testid="nav-gym-login"
                className="hidden lg:inline-flex btn-green px-4 py-2 rounded-sm text-xs"
              >
                Gym Owner Login
              </Link>
              <AccountLink />
              <Link to="/cart" data-testid="nav-cart" className="relative text-zinc-200 hover:text-yellow-400 transition-colors">
                <ShoppingCart className="h-5 w-5" />
                {count > 0 && (
                  <span
                    data-testid="cart-count"
                    className="absolute -top-2 -right-2 bg-yellow-400 text-black text-[10px] font-bold rounded-full h-4 min-w-4 px-1 flex items-center justify-center"
                  >
                    {count}
                  </span>
                )}
              </Link>
            </div>
          </div>
        </div>
        <nav className="hidden lg:block border-t border-white/5 bg-black/60">
          <div className="max-w-[1400px] mx-auto px-8 flex items-center gap-5 h-11 overflow-x-auto">
            {NAV.map((n) => (
              <Link
                key={n.label}
                to={n.to}
                data-testid={`nav-${n.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className={`whitespace-nowrap font-heading font-semibold uppercase text-sm tracking-wide transition-colors ${n.accent === "yellow" ? "text-yellow-400 hover:text-yellow-300" : "text-zinc-300 hover:text-yellow-400"}`}
              >
                {n.label}
              </Link>
            ))}
          </div>
        </nav>
      </header>

      <main className="flex-1">
        <Outlet />
      </main>

      <footer className="border-t border-white/10 bg-black mt-16">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-8 py-12 grid grid-cols-2 md:grid-cols-4 gap-8">
          <div className="col-span-2 md:col-span-1">
            <Logo />
            <p className="text-zinc-500 text-sm mt-4 max-w-xs">
              Official Paragon gear. Built for the team.
            </p>
            <p className="text-zinc-400 text-sm mt-2 max-w-xs font-semibold">
              One team. Multiple academies. One official store.
            </p>
          </div>
          <div>
            <h5 className="font-heading font-bold uppercase text-white mb-3 tracking-wide">Shop</h5>
            <ul className="space-y-2 text-sm text-zinc-400">
              <li><Link to="/shop" className="hover:text-yellow-400">All Products</Link></li>
              <li><Link to="/shop?round=round-1" className="hover:text-yellow-400">Winter '26</Link></li>
              <li><Link to="/shop?category=Rash%20Guards" className="hover:text-yellow-400">Rash Guards</Link></li>
              <li><Link to="/shop?category=No-Gi" className="hover:text-yellow-400">No-Gi</Link></li>
              <li><Link to="/shop?category=Hoodies" className="hover:text-yellow-400">Tees &amp; Hoodies</Link></li>
              <li><Link to="/shop?category=Gis" className="hover:text-yellow-400">Gis</Link></li>
              <li><Link to="/shop?audience=Youth" className="hover:text-yellow-400">Youth</Link></li>
              <li><Link to="/shop?category=Accessories" className="hover:text-yellow-400">Accessories</Link></li>
            </ul>
          </div>
          <div>
            <h5 className="font-heading font-bold uppercase text-white mb-3 tracking-wide">Account</h5>
            <ul className="space-y-2 text-sm text-zinc-400">
              <li><Link to="/account" className="hover:text-yellow-400">My Orders</Link></li>
              <li><Link to="/gym/login" className="hover:text-yellow-400">Gym Owner Login</Link></li>
            </ul>
          </div>
          <div>
            <h5 className="font-heading font-bold uppercase text-white mb-3 tracking-wide">Team</h5>
            <ul className="space-y-2 text-sm text-zinc-400">
              <li><Link to="/academies" className="hover:text-yellow-400">Find Your Academy</Link></li>
              <li><Link to="/academies" className="hover:text-yellow-400">Academies &amp; Affiliates</Link></li>
              <li><Link to="/gym/login" className="hover:text-emerald-400">Gym Owner Wholesale</Link></li>
            </ul>
          </div>
        </div>
        <div className="border-t border-white/10 py-5 text-center text-xs text-zinc-600 font-mono uppercase tracking-widest">
          © 2026 Paragon Brazilian Jiu-Jitsu · Official Team Store
        </div>
      </footer>
    </div>
  );
}
